/**
 * Linked discipline schedules for one macro activity.
 *
 * Shows the Engineering, Procurement, Fabrication & Construction and
 * Pre-commissioning & Commissioning activities tied to a macro activity, each
 * with its own stage-gate progress, and pulls that progress up into the macro
 * schedule on demand. Nothing is written until Pull is pressed.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import {
  Loader2, RefreshCw, Link2, Trash2, ChevronDown, ChevronRight, Plus, History, Scale, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export const DISCIPLINE_ORDER = ['ENGINEERING', 'PROCUREMENT', 'FC', 'PRECOMM_COMM'];
export const DISCIPLINE_META = {
  ENGINEERING: { short: 'E', label: 'Engineering', colour: '#38BDF8' },
  PROCUREMENT: { short: 'P', label: 'Procurement', colour: '#A78BFA' },
  FC: { short: 'F&C', label: 'Fabrication & Construction', colour: '#FB923C' },
  PRECOMM_COMM: { short: 'PC', label: 'Pre-comm & Commissioning', colour: '#34D399' },
};

const pct = (v) => (v == null ? '—' : `${Math.round(v * 10) / 10}%`);
const shortDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' });
};

export function MacroLinksDrawer({ open, onOpenChange, scheduleId, activity, onPulled }) {
  const activityId = activity?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pulling, setPulling] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [weightsOpen, setWeightsOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState(null);
  const [linkFor, setLinkFor] = useState(null);
  const [openGates, setOpenGates] = useState({});

  const load = useCallback(async () => {
    if (!scheduleId || !activityId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/macro-links/schedules/${scheduleId}/activities/${activityId}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the linked schedules');
    } finally { setLoading(false); }
  }, [scheduleId, activityId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const preview = data?.rollup?.macroProgressPct;
  const current = data?.currentProgressPct ?? 0;
  const totalLinks = useMemo(
    () => (data?.disciplines || []).reduce((n, d) => n + (d.links?.length || 0), 0), [data]);

  const pull = async (disciplines) => {
    setPulling(true);
    try {
      const { data: res } = await api.post(
        `/macro-links/schedules/${scheduleId}/activities/${activityId}/pull`,
        disciplines ? { disciplines } : {});
      toast.success(`Macro progress updated ${pct(res.before)} → ${pct(res.after)}`);
      onPulled?.(activityId, res.after);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pull failed');
    } finally { setPulling(false); }
  };

  const removeLink = async (linkId) => {
    try {
      await api.delete(`/macro-links/schedules/${scheduleId}/links/${linkId}`);
      load();
    } catch { toast.error('Could not remove the link'); }
  };

  const openHistory = async () => {
    try {
      const { data: h } = await api.get(`/macro-links/schedules/${scheduleId}/activities/${activityId}/history`);
      setHistoryRows(h.history || []);
    } catch { toast.error('Could not load the pull history'); }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-3xl p-0 flex flex-col" data-testid="macro-links-drawer">
          <SheetHeader className="px-5 py-4 border-b border-border shrink-0">
            <SheetTitle className="text-base flex items-center gap-2 pr-8">
              <Link2 className="w-4 h-4 text-sky-400 shrink-0" />
              <span className="truncate">{activity?.name || 'Linked schedules'}</span>
            </SheetTitle>
            <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground">
              {activity?.wbs_code && <Badge variant="outline" className="text-[9px] font-mono">{activity.wbs_code}</Badge>}
              <span>{totalLinks} linked activit{totalLinks === 1 ? 'y' : 'ies'}</span>
              <span>· macro now <strong className="text-foreground">{pct(current)}</strong></span>
              {preview != null && (
                <span>· pull would set <strong style={{ color: preview >= current ? '#4ADE80' : '#F87171' }}>{pct(preview)}</strong></span>
              )}
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="macro-links-body">
            {loading && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin text-sky-400" />Reading the discipline schedules…
              </div>
            )}

            {!loading && (data?.disciplines || []).map((d) => {
              const links = d.links || [];
              const isOpen = expanded[d.key] ?? links.length > 0;
              return (
                <div key={d.key} className="rounded-lg border border-border overflow-hidden"
                     data-testid={`discipline-block-${d.key}`}>
                  <div className="flex items-center gap-2 px-3 py-2 bg-muted/40">
                    <button onClick={() => setExpanded(e => ({ ...e, [d.key]: !isOpen }))}
                            className="text-muted-foreground" data-testid={`discipline-toggle-${d.key}`}>
                      {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                    <span className="inline-flex items-center justify-center text-[10px] font-bold rounded px-1.5 py-0.5"
                          style={{ background: `${d.colour}22`, color: d.colour }}>{d.short}</span>
                    <span className="text-xs font-medium text-foreground">{d.label}</span>
                    <Badge variant="outline" className="text-[9px]">{links.length}</Badge>
                    <div className="ml-auto flex items-center gap-3">
                      <PvaStrip d={d} />
                      <span className="text-[10px] text-muted-foreground">weight {d.weight}%
                        {d.effectiveWeight > 0 && d.effectiveWeight !== d.weight && ` → ${d.effectiveWeight}%`}</span>
                      <span className="text-xs font-semibold" style={{ color: d.colour }}>{pct(d.progressPct)}</span>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]"
                              onClick={() => setLinkFor(d.key)} data-testid={`link-add-${d.key}`}>
                        <Plus className="w-3 h-3 mr-0.5" />Link
                      </Button>
                      {links.length > 0 && (
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-sky-400"
                                disabled={pulling} onClick={() => pull([d.key])}
                                data-testid={`pull-discipline-${d.key}`}>
                          <RefreshCw className="w-3 h-3 mr-0.5" />Pull
                        </Button>
                      )}
                    </div>
                  </div>

                  {isOpen && (links.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground px-3 py-3">
                      Nothing linked from this schedule yet.
                    </p>
                  ) : (
                    <table className="w-full text-[11px]">
                      <thead className="text-muted-foreground uppercase text-[9px]">
                        <tr>{['', 'Code', 'Activity', 'Planned', 'Actual', d.key === 'FC' ? 'Plan vs actual qty' : 'Plan vs actual %', 'Var', 'Wt%', 'Progress', ''].map((h, hi) => (
                          <th key={hi} className="text-left px-2 py-1 font-medium">{h}</th>))}</tr>
                      </thead>
                      <tbody>
                        {links.map((l) => {
                          const pva = l.planVsActual || {};
                          const gatesOpen = openGates[l.linkId];
                          return (
                          <>
                          <tr key={l.linkId} className="border-t border-border align-top"
                              data-testid={`link-row-${l.linkId}`}>
                            <td className="px-1 py-1.5">
                              {(l.stageGates || []).length > 0 && (
                                <button onClick={() => setOpenGates(g => ({ ...g, [l.linkId]: !gatesOpen }))}
                                        className="text-muted-foreground hover:text-sky-300"
                                        title="Stage-gate detail"
                                        data-testid={`gates-toggle-${l.linkId}`}>
                                  {gatesOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                </button>
                              )}
                            </td>
                            <td className="px-2 py-1.5 font-mono text-[10px] text-sky-300">{l.code || '—'}</td>
                            <td className="px-2 py-1.5 text-foreground max-w-[180px]">
                              <div className="truncate" title={l.name}>{l.name}</div>
                              {l.discipline_tag && <div className="text-[9px] text-muted-foreground">{l.discipline_tag}{l.area ? ` · ${l.area}` : ''}</div>}
                            </td>
                            <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">
                              {shortDate(l.plannedStart)}{l.plannedFinish ? ` → ${shortDate(l.plannedFinish)}` : ''}
                            </td>
                            <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">
                              {shortDate(l.actualStart)}{l.actualFinish ? ` → ${shortDate(l.actualFinish)}` : ''}
                            </td>
                            <td className="px-2 py-1.5" data-testid={`pva-${l.linkId}`}>
                              {d.key === 'FC' && pva.scopeQuantity ? (
                                <div className="text-[10px] whitespace-nowrap">
                                  <span className="text-sky-300">{pva.planQty}</span>
                                  <span className="text-muted-foreground"> / </span>
                                  <span className="text-amber-300">{pva.forecastQty}</span>
                                  <span className="text-muted-foreground"> / </span>
                                  <span className="text-emerald-300">{pva.actualQty}</span>
                                  <span className="text-muted-foreground"> {pva.uom}</span>
                                  <div className="text-muted-foreground">of {pva.scopeQuantity}{pva.spiQuantity ? ` · SPI ${pva.spiQuantity}` : ''}</div>
                                </div>
                              ) : <PvaBar pva={pva} />}
                            </td>
                            <td className="px-2 py-1.5">
                              {pva.variancePct == null ? <span className="text-muted-foreground">—</span> : (
                                <span className="font-semibold" style={{ color: pva.variancePct < -1 ? '#F87171' : pva.variancePct > 1 ? '#4ADE80' : '#94A3B8' }}>
                                  {pva.variancePct > 0 ? '+' : ''}{Math.round(pva.variancePct * 10) / 10}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-muted-foreground">{Math.round(l.weight)}%</td>
                            <td className="px-2 py-1.5">
                              <div className="flex items-center gap-1.5">
                                <Progress value={l.progressPct} className="h-1.5 w-14" />
                                <span className="font-semibold text-foreground">{pct(l.progressPct)}</span>
                              </div>
                              <div className="text-[9px] text-muted-foreground">{l.status}</div>
                            </td>
                            <td className="px-2 py-1.5">
                              <button onClick={() => removeLink(l.linkId)} title="Remove this link"
                                      className="text-muted-foreground hover:text-red-400"
                                      data-testid={`link-remove-${l.linkId}`}>
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </td>
                          </tr>
                          {gatesOpen && (
                            <tr key={`${l.linkId}-gates`} className="bg-muted/20">
                              <td />
                              <td colSpan={9} className="px-2 py-2" data-testid={`gates-${l.linkId}`}>
                                <table className="w-full text-[10px]">
                                  <thead className="text-muted-foreground uppercase text-[9px]">
                                    <tr>{['Stage gate', 'Wt%', 'Planned', 'Forecast', 'Actual', 'Status', 'Progress'].map(h => (
                                      <th key={h} className="text-left px-2 py-0.5 font-medium">{h}</th>))}</tr>
                                  </thead>
                                  <tbody>
                                    {(l.stageGates || []).map((g, gi) => (
                                      <tr key={gi} className="border-t border-border/50">
                                        <td className="px-2 py-1 text-foreground">{g.name}</td>
                                        <td className="px-2 py-1 text-muted-foreground">{g.weight}</td>
                                        <td className="px-2 py-1 text-sky-300">{shortDate(g.plannedDate)}</td>
                                        <td className="px-2 py-1 text-amber-300">{shortDate(g.forecastDate)}</td>
                                        <td className="px-2 py-1 text-emerald-300">{shortDate(g.actualDate)}</td>
                                        <td className="px-2 py-1 text-muted-foreground">{g.status || '—'}</td>
                                        <td className="px-2 py-1">
                                          <span className="font-semibold" style={{ color: g.progress >= 100 ? '#4ADE80' : g.progress > 0 ? '#FBBF24' : '#64748B' }}>
                                            {Math.round(g.progress)}%
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                          </>
                          );
                        })}
                      </tbody>
                    </table>
                  ))}
                </div>
              );
            })}

            {!loading && data?.rollup?.weightsRenormalised && (
              <p className="text-[10px] text-amber-400">
                Only {data.rollup.linkedDisciplines.length} of 4 schedules are linked, so their weights were
                scaled up to 100% for this activity.
              </p>
            )}
          </div>

          <div className="border-t border-border px-5 py-3 flex items-center gap-2 flex-wrap shrink-0">
            <Button size="sm" className="bg-sky-600 hover:bg-sky-700" disabled={pulling || !totalLinks}
                    onClick={() => pull(null)} data-testid="pull-all-disciplines">
              {pulling ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Pull progress from all schedules
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => setWeightsOpen(true)}
                    data-testid="open-weights">
              <Scale className="w-3.5 h-3.5 mr-1" />Discipline weights
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={openHistory}
                    data-testid="open-pull-history">
              <History className="w-3.5 h-3.5 mr-1" />Pull history
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {weightsOpen && (
        <WeightsDialog open={weightsOpen} onOpenChange={setWeightsOpen} scheduleId={scheduleId}
                       activityId={activityId} initial={data?.weights} onSaved={load} />
      )}
      {historyRows && (
        <HistoryDialog rows={historyRows} onClose={() => setHistoryRows(null)} />
      )}
      {linkFor && (
        <LinkPickerDialog discipline={linkFor} scheduleId={scheduleId} activityId={activityId}
                          linkedIds={(data?.disciplines || []).find(d => d.key === linkFor)?.links?.map(l => l.microId) || []}
                          onClose={() => setLinkFor(null)} onLinked={load} />
      )}
    </>
  );
}

function WeightsDialog({ open, onOpenChange, scheduleId, activityId, initial, onSaved }) {
  const [w, setW] = useState(() => {
    const out = {};
    DISCIPLINE_ORDER.forEach(k => { out[k] = String(initial?.[k] ?? 0); });
    return out;
  });
  const [applyAll, setApplyAll] = useState(false);
  const total = DISCIPLINE_ORDER.reduce((s, k) => s + (parseFloat(w[k]) || 0), 0);
  const save = async () => {
    try {
      const weights = {};
      DISCIPLINE_ORDER.forEach(k => { weights[k] = parseFloat(w[k]) || 0; });
      await api.put(`/macro-links/schedules/${scheduleId}/activities/${activityId}/weights`,
        { weights, apply_to_schedule: applyAll });
      toast.success(applyAll ? 'Weights saved for the whole schedule' : 'Weights saved for this activity');
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save the weights'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="weights-dialog">
        <DialogHeader><DialogTitle className="text-base">Discipline weights</DialogTitle></DialogHeader>
        <p className="text-[11px] text-muted-foreground -mt-2">
          How much each schedule contributes to this activity's progress. Schedules with no links are
          dropped and the rest scaled up.
        </p>
        <div className="space-y-2">
          {DISCIPLINE_ORDER.map((k) => (
            <div key={k} className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center text-[10px] font-bold rounded px-1.5 py-0.5 w-10"
                    style={{ background: `${DISCIPLINE_META[k].colour}22`, color: DISCIPLINE_META[k].colour }}>
                {DISCIPLINE_META[k].short}
              </span>
              <span className="text-xs flex-1">{DISCIPLINE_META[k].label}</span>
              <Input type="number" min="0" max="100" value={w[k] ?? ''} className="w-20 h-8 text-xs"
                     onChange={(e) => setW(p => ({ ...p, [k]: e.target.value }))}
                     data-testid={`weight-input-${k}`} />
            </div>
          ))}
          <div className={`text-xs text-right ${Math.abs(total - 100) > 0.5 ? 'text-red-400' : 'text-emerald-400'}`}
               data-testid="weights-total">
            Total {Math.round(total * 10) / 10}%
          </div>
          <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <Checkbox checked={applyAll} onCheckedChange={setApplyAll} data-testid="weights-apply-all" />
            Use these as the default for every activity in this schedule
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={Math.abs(total - 100) > 0.5} data-testid="weights-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ rows, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="pull-history-dialog">
        <DialogHeader><DialogTitle className="text-base">Pull history</DialogTitle></DialogHeader>
        {rows.length === 0
          ? <p className="text-xs text-muted-foreground">This activity has never been pulled.</p>
          : (
            <ScrollArea className="max-h-80">
              <table className="w-full text-[11px]">
                <thead className="text-muted-foreground uppercase text-[9px]">
                  <tr>{['When', 'By', 'Before', 'After', 'Schedules'].map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-2 py-1.5">{new Date(r.pulledAt).toLocaleString()}</td>
                      <td className="px-2 py-1.5">{r.pulledBy}</td>
                      <td className="px-2 py-1.5">{pct(r.before)}</td>
                      <td className="px-2 py-1.5 font-semibold text-foreground">{pct(r.after)}</td>
                      <td className="px-2 py-1.5 text-muted-foreground">
                        {(Array.isArray(r.scope) ? r.scope : Object.keys(r.disciplines || {}))
                          .map(k => DISCIPLINE_META[k]?.short || k).join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          )}
      </DialogContent>
    </Dialog>
  );
}

function LinkPickerDialog({ discipline, scheduleId, activityId, onClose, onLinked, linkedIds = [] }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let dead = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/macro-links/schedules/${scheduleId}/candidates`,
          { params: { discipline, q, limit: 100 } });
        if (!dead) setRows(data.candidates || []);
      } catch { if (!dead) toast.error('Could not load activities'); }
      finally { if (!dead) setLoading(false); }
    }, q ? 350 : 0);
    return () => { dead = true; clearTimeout(t); };
  }, [scheduleId, discipline, q]);

  const link = async () => {
    try {
      const { data } = await api.post(
        `/macro-links/schedules/${scheduleId}/activities/${activityId}/links`,
        { discipline, micro_ids: picked });
      toast.success(`${data.created} activit${data.created === 1 ? 'y' : 'ies'} linked`);
      onClose(); onLinked?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not link'); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="link-picker-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Link {DISCIPLINE_META[discipline]?.label} activities</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by code or description"
                 className="pl-7 h-8 text-xs" data-testid="link-picker-search" />
        </div>
        <ScrollArea className="h-72 border border-border rounded">
          {loading ? (
            <div className="p-4 text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground">No activities found in this schedule.</p>
          ) : rows.map((r) => {
            const already = linkedIds.includes(r.microId);
            return (
              <label key={r.microId}
                     className={`flex items-center gap-2 px-3 py-1.5 text-xs border-b border-border/50 ${
                       already ? 'opacity-50' : 'hover:bg-muted/50 cursor-pointer'}`}>
                <Checkbox checked={already || picked.includes(r.microId)} disabled={already}
                          onCheckedChange={(v) => setPicked(p => v ? [...p, r.microId] : p.filter(x => x !== r.microId))}
                          data-testid={`candidate-${r.microId}`} />
                <span className="font-mono text-[10px] text-sky-300 w-28 truncate">{r.code}</span>
                <span className="flex-1 truncate text-foreground">{r.name}</span>
                {already && <Badge variant="outline" className="text-[9px]">linked</Badge>}
                <span className="text-muted-foreground">{pct(r.progressPct)}</span>
              </label>
            );
          })}
        </ScrollArea>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={link} disabled={!picked.length} data-testid="link-picker-confirm">
            <Link2 className="w-3.5 h-3.5 mr-1" />Link {picked.length || ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Plan vs forecast vs actual as a compact three-tone bar. */
function PvaBar({ pva }) {
  if (!pva || pva.plannedPct == null) return <span className="text-muted-foreground text-[10px]">—</span>;
  return (
    <div className="text-[10px] whitespace-nowrap">
      <div className="relative h-2.5 w-24 rounded-sm bg-slate-800 overflow-hidden">
        <span className="absolute inset-y-0 left-0 bg-emerald-500/70" style={{ width: `${Math.min(100, pva.actualPct || 0)}%` }} />
        <span className="absolute inset-y-0 w-[2px] bg-sky-300" style={{ left: `${Math.min(100, pva.plannedPct)}%` }} title={`Plan ${pva.plannedPct}%`} />
      </div>
      <span className="text-sky-300">P {Math.round(pva.plannedPct)}</span>
      <span className="text-muted-foreground"> · </span>
      <span className="text-emerald-300">A {Math.round(pva.actualPct || 0)}</span>
    </div>
  );
}

/** Discipline-level plan vs actual headline. */
function PvaStrip({ d }) {
  if (d.plannedPct == null && d.actualPct == null) return null;
  const variance = d.variancePct;
  return (
    <span className="text-[10px] whitespace-nowrap" data-testid={`discipline-pva-${d.key}`}>
      <span className="text-muted-foreground">plan </span>
      <span className="text-sky-300 font-semibold">{pct(d.plannedPct)}</span>
      {d.forecastPct != null && (
        <>
          <span className="text-muted-foreground"> · fc </span>
          <span className="text-amber-300 font-semibold">{pct(d.forecastPct)}</span>
        </>
      )}
      <span className="text-muted-foreground"> · actual </span>
      <span className="text-emerald-300 font-semibold">{pct(d.actualPct)}</span>
      {variance != null && (
        <span className="ml-1 font-semibold" style={{ color: variance < -1 ? '#F87171' : variance > 1 ? '#4ADE80' : '#94A3B8' }}>
          ({variance > 0 ? '+' : ''}{Math.round(variance * 10) / 10})
        </span>
      )}
    </span>
  );
}

/** The LINKS cell: one chip per discipline with its linked count and progress. */
export function DisciplineChips({ summary, onOpen, testId }) {
  if (!summary || !summary.totalLinks) {
    return (
      <button onClick={onOpen} className="text-[10px] text-slate-500 hover:text-sky-300" data-testid={testId}>
        + link
      </button>
    );
  }
  return (
    <button onClick={onOpen} className="flex items-center gap-1" data-testid={testId}
            title="Open the linked Engineering / Procurement / F&C / Pre-comm schedules">
      {DISCIPLINE_ORDER.map((k) => {
        const d = summary.disciplines?.[k];
        const meta = DISCIPLINE_META[k];
        const live = d?.count > 0;
        return (
          <span key={k}
                className="inline-flex items-center gap-0.5 rounded px-1 text-[9px] font-semibold leading-4"
                style={{
                  background: live ? `${meta.colour}22` : 'transparent',
                  color: live ? meta.colour : '#475569',
                  border: `1px solid ${live ? `${meta.colour}55` : '#1e293b'}`,
                }}>
            {meta.short}
            {live && <span className="opacity-80">{Math.round(d.progressPct ?? 0)}</span>}
          </span>
        );
      })}
    </button>
  );
}
